// GAVION contact-call: « Appeler » next to a contact's phone number. Opens the dialer deep link
// /appeler?company_id=&contact_id=&phone=; the dialer (claim_contact_call) enforces every rule. The current page
// travels in the router state so the dialer comes back here after the outcome.
import { ChevronDown, Phone } from "lucide-react";
import { useRecordContext, useTranslate, type Identifier } from "ra-core";
import { useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { translatePersonalInfoTypeLabel } from "@/components/atomic-crm/contacts/contactModel";
import {
  contactCallPath,
  contactDisplayName,
  dialablePhones,
} from "../domain/dialer/contactCall";
import { formatPhoneDisplay, normalizePhone } from "../domain/phone";

type Props = {
  /** The contact's lead; no lead → nothing to call from the dialer. */
  companyId: Identifier | null | undefined;
  contactId: Identifier;
  contactName: string;
  /** The number as stored in phone_jsonb (any spelling). */
  phone: string | null | undefined;
};

export const CallContactButton = ({
  companyId,
  contactId,
  contactName,
  phone,
}: Props) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const location = useLocation();
  const e164 = normalizePhone(phone);
  if (!e164 || companyId == null || companyId === "") return null;
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="h-7 px-2"
      aria-label={t("gavion.dialer.contact_call.call_label", {
        contact: contactName || t("gavion.dialer.contact_call.unnamed"),
        phone: formatPhoneDisplay(e164),
      })}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation(); // inside clickable list rows
        navigate(contactCallPath(companyId, contactId, e164), {
          state: { from: location.pathname + location.search },
        });
      }}
    >
      <Phone className="size-3.5" aria-hidden="true" />
      {t("gavion.dialer.contact_call.call")}
    </Button>
  );
};

/**
 * Inside a phone_jsonb ArrayField row (Atomic ContactPersonalInfo): the row record is { number, type }, the
 * contact comes from the caller.
 */
export const CallContactPhoneRowButton = ({
  contact,
}: {
  contact: {
    id: Identifier;
    company_id?: Identifier | null;
    first_name?: string | null;
    last_name?: string | null;
  };
}) => {
  const row = useRecordContext<{ number?: string }>();
  return (
    <CallContactButton
      companyId={contact.company_id}
      contactId={contact.id}
      contactName={contactDisplayName(contact)}
      phone={row?.number}
    />
  );
};

/**
 * Follow-up M6 (contacts list row): « Appeler » for a contact with any number of phones. One dialable number → the
 * plain one-click button; several → a menu listing type + number, the rep picks which one to call.
 */
export const CallContactPhonesButton = ({
  companyId,
  contactId,
  contactName,
  phones,
}: {
  companyId: Identifier | null | undefined;
  contactId: Identifier;
  contactName: string;
  /** contacts.phone_jsonb as stored ([{ number, type }]). */
  phones: unknown;
}) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const location = useLocation();
  const dialable = dialablePhones(phones);
  if (dialable.length === 0 || companyId == null || companyId === "")
    return null;
  if (dialable.length === 1)
    return (
      <CallContactButton
        companyId={companyId}
        contactId={contactId}
        contactName={contactName}
        phone={dialable[0].e164}
      />
    );
  const name = contactName || t("gavion.dialer.contact_call.unnamed");
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2"
          aria-label={t("gavion.dialer.contact_call.choose_number_label", {
            contact: name,
          })}
          onClick={(e) => e.stopPropagation()} // inside clickable list rows
        >
          <Phone className="size-3.5" aria-hidden="true" />
          {t("gavion.dialer.contact_call.call")}
          <ChevronDown className="size-3.5" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {dialable.map((p) => (
          <DropdownMenuItem
            key={p.e164}
            onClick={(e) => e.stopPropagation()}
            onSelect={() =>
              navigate(contactCallPath(companyId, contactId, p.e164), {
                state: { from: location.pathname + location.search },
              })
            }
          >
            {p.type ? (
              <span className="text-muted-foreground">
                {translatePersonalInfoTypeLabel(p.type, t)}
              </span>
            ) : null}{" "}
            <span className="tabular-nums">{formatPhoneDisplay(p.e164)}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
